import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Card, CardContent, Grid2 as Grid, MenuItem, Skeleton, Stack, TextField, Typography,
} from '@mui/material';
import { getPlatformStats, type PlatformStats } from '../../lib/platform-stats-api';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';

const pct = (occ: number, total: number) => (total > 0 ? Math.round((occ / total) * 100) : 0);

export function PlatformDashboardPage() {
  const t = useT();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [devId, setDevId] = useState('all');
  const [clusterId, setClusterId] = useState('all');

  useEffect(() => {
    getPlatformStats()
      .then(setStats)
      .catch((e) => setError(extractApiError(e).message))
      .finally(() => setLoading(false));
  }, []);

  const totals = stats?.totals;
  const tiles: { label: string; value: number | undefined; sub?: string }[] = [
    { label: 'plt.tileDevelopers', value: totals?.developers, sub: totals ? t('plt.tileArchived', { count: totals.developers_archived }) : undefined },
    { label: 'plt.tileClusters', value: totals?.clusters },
    { label: 'plt.tileUnits', value: totals?.units, sub: t('plt.tileUnitsInActive') },
    { label: 'plt.tileOccupied', value: totals?.occupied_units, sub: totals ? t('plt.tileOccupiedPct', { pct: pct(totals.occupied_units, totals.units) }) : undefined },
    { label: 'plt.tileResidents', value: totals?.residents, sub: t('plt.tileResidentsSub') },
    { label: 'plt.tileAdmins', value: totals?.admin_accounts, sub: t('plt.tileAdminsSub') },
    { label: 'plt.tileSecurity', value: totals?.security, sub: t('plt.tileSecuritySub') },
    { label: 'plt.tileInviteCodes', value: totals?.active_invite_codes, sub: t('plt.tileInviteCodesSub') },
  ];

  const selectedDev = devId === 'all' ? null : stats?.developers.find((d) => d.id === devId) ?? null;
  const clusterOptions = selectedDev?.clusters ?? [];
  const occ = useMemo(() => {
    if (!stats) return { occupied: 0, units: 0 };
    if (devId === 'all') return { occupied: stats.totals.occupied_units, units: stats.totals.units };
    if (!selectedDev) return { occupied: 0, units: 0 };
    if (clusterId === 'all') return { occupied: selectedDev.occupied_units, units: selectedDev.units };
    const cl = selectedDev.clusters.find((c2) => c2.id === clusterId);
    return cl ? { occupied: cl.occupied_units, units: cl.units } : { occupied: 0, units: 0 };
  }, [stats, devId, clusterId, selectedDev]);
  const occPct = pct(occ.occupied, occ.units);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={600}>{t('plt.overviewTitle')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('plt.overviewSubtitle')}</Typography>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Grid container spacing={2}>
        {tiles.map((tile) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={tile.label}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">{t(tile.label)}</Typography>
                <Typography variant="h4" sx={{ mt: 1 }}>
                  {loading ? <Skeleton variant="text" width={48} /> : tile.value ?? '—'}
                </Typography>
                {tile.sub && !loading ? <Typography variant="caption" color="text.secondary">{tile.sub}</Typography> : null}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {stats ? (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('plt.occupancyTitle')}</Typography>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  <TextField select size="small" label={t('plt.tileDevelopers')} value={devId}
                    onChange={(e) => { setDevId(e.target.value); setClusterId('all'); }} fullWidth>
                    <MenuItem value="all">{t('plt.allDevelopers')}</MenuItem>
                    {stats.developers.map((d) => (<MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>))}
                  </TextField>
                  <TextField select size="small" label={t('plt.colCluster')} value={clusterId}
                    onChange={(e) => setClusterId(e.target.value)} fullWidth disabled={devId === 'all'}>
                    <MenuItem value="all">{t('plt.allClusters')}</MenuItem>
                    {clusterOptions.map((cl) => (<MenuItem key={cl.id} value={cl.id}>{cl.name}</MenuItem>))}
                  </TextField>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Donut percent={occPct} />
                  <Box>
                    <Typography variant="body1"><strong>{occ.occupied} / {occ.units}</strong> {t('plt.occupied')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {devId === 'all' ? t('plt.scopeAll') : clusterId === 'all' ? t('plt.scopeDev') : t('plt.scopeCluster')}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('plt.chartTitle')}</Typography>
                {stats.developers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">{t('plt.noDevs')}</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {stats.developers.map((d) => {
                      const p = pct(d.occupied_units, d.units);
                      return (
                        <Stack key={d.id} direction="row" alignItems="center" spacing={1.5}>
                          <Typography variant="body2" sx={{ width: 150, flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</Typography>
                          <Box sx={{ flex: 1, position: 'relative', height: 18, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Box sx={{ position: 'absolute', inset: 0, width: `${p}%`, bgcolor: 'primary.main', opacity: 0.7, borderRadius: 1 }} />
                          </Box>
                          <Typography variant="body2" sx={{ width: 64, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{d.occupied_units}/{d.units}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ width: 36, textAlign: 'right' }}>{p}%</Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}
    </Stack>
  );
}

function Donut({ percent }: { percent: number }) {
  return (
    <svg width="104" height="104" viewBox="0 0 42 42" aria-label={`${percent}% occupied`}>
      <circle cx="21" cy="21" r="15.9155" fill="none" stroke="rgba(125,125,125,0.18)" strokeWidth="6" />
      <circle cx="21" cy="21" r="15.9155" fill="none" stroke="#3C5A75" strokeWidth="6"
        strokeDasharray={`${percent} ${100 - percent}`} strokeDashoffset="25" strokeLinecap="round" />
      <text x="21" y="22.5" textAnchor="middle" fontSize="9" fontWeight="700" fill="currentColor">{percent}%</text>
    </svg>
  );
}
